/**
 * Gate metric registry — the metrics a "today's drops" rule can read. A sent
 * drop has two sets of numbers, joined by its unique id:
 *  - tracker: the drop's row in its campaign's tracker — the SAME ids, names and
 *    formulas as the list's / gateway's stats (the Flow formula catalog), so
 *    "CR" means conversions per 100 visits everywhere (CV = per 100 clicks);
 *  - smswiser: SMSWiser's stats for the drop (sent, delivered, bot clicks…);
 *  - both: a ratio that needs one number from each side.
 * Units: `percent` is stored 0–100, `ratio` is stored 0–1 (typed in %).
 * For Media Devoted | Created By Ciro Autiero
 */

export const GATE_METRICS = [
  // Tracker (the campaign's tracker, the drop's own row) — order = the Flow catalog's.
  { id: 'impressions', label: 'Impressions', group: 'tracker', format: 'int' },
  { id: 'ImCTR', label: 'SMS CTR', group: 'tracker', format: 'percent' },
  { id: 'Click2Reg', label: 'Click2Reg', group: 'tracker', format: 'percent' },
  { id: 'Reg2Dep', label: 'Reg2Dep', group: 'tracker', format: 'percent' },
  { id: 'uEPV', label: 'uEPV', group: 'tracker', format: 'money' },
  { id: 'CPuC', label: 'CPuC', group: 'tracker', format: 'money' },
  { id: 'CPL', label: 'CPL', group: 'tracker', format: 'money' },
  { id: 'CPS', label: 'CPS', group: 'tracker', format: 'money' },

  { id: 'visits', label: 'Visits', group: 'tracker', format: 'int' },
  { id: 'uniqueVisits', label: 'Unique Visits', group: 'tracker', format: 'int' },
  { id: 'clicks', label: 'Clicks', group: 'tracker', format: 'int' },
  { id: 'uniqueClicks', label: 'Unique Clicks', group: 'tracker', format: 'int' },
  { id: 'conversions', label: 'Conversions', group: 'tracker', format: 'int' },
  { id: 'registrations', label: 'Registrations', group: 'tracker', format: 'int' },
  { id: 'ftd', label: 'FTD', group: 'tracker', format: 'int' },
  { id: 'revenue', label: 'Revenue', group: 'tracker', format: 'money' },
  { id: 'cost', label: 'Cost', group: 'tracker', format: 'money' },
  { id: 'profit', label: 'Profit', group: 'tracker', format: 'money' },
  { id: 'roi', label: 'ROI', group: 'tracker', format: 'percent' },
  { id: 'ctr', label: 'CTR', group: 'tracker', format: 'percent' },
  { id: 'cr', label: 'CR', group: 'tracker', format: 'percent' },
  { id: 'cv', label: 'CV', group: 'tracker', format: 'percent' },
  { id: 'epv', label: 'EPV', group: 'tracker', format: 'money' },
  { id: 'epc', label: 'EPC', group: 'tracker', format: 'money' },
  { id: 'epl', label: 'EPL', group: 'tracker', format: 'money' },
  { id: 'uEPC', label: 'Unique EPC', group: 'tracker', format: 'money' },
  { id: 'uctr', label: 'Unique CTR', group: 'tracker', format: 'percent' },
  { id: 'ucr', label: 'Unique CR', group: 'tracker', format: 'percent' },
  { id: 'cpv', label: 'CPV', group: 'tracker', format: 'money' },
  { id: 'cpc', label: 'CPC', group: 'tracker', format: 'money' },
  { id: 'ecpa', label: 'eCPA', group: 'tracker', format: 'money' },
  { id: 'ecpm', label: 'eCPM', group: 'tracker', format: 'money' },
  { id: 'rpm', label: 'RPM', group: 'tracker', format: 'money' },
  // Tracker extras the drop rules always had (not in the Flow catalog).
  { id: 'suspiciousClicks', label: 'Suspicious Clicks', group: 'tracker', format: 'int' },
  { id: 'suspiciousClicksRatio', label: 'Suspicious Clicks Ratio', group: 'tracker', format: 'ratio' },
  { id: 'uniqueClickRatio', label: 'Unique Click Ratio', group: 'tracker', format: 'ratio' },
  // SMSWiser (the drop's own stats)
  { id: 'smsSent', label: 'SMS Sent', group: 'smswiser', format: 'int' },
  { id: 'smsDelivered', label: 'Delivered', group: 'smswiser', format: 'int' },
  { id: 'smsUndeliverable', label: 'Undeliverable', group: 'smswiser', format: 'int' },
  { id: 'smsClicks', label: 'SMS Clicks', group: 'smswiser', format: 'int' },
  { id: 'smsBotClicks', label: 'Bot Clicks', group: 'smswiser', format: 'int' },
  { id: 'smsCtr', label: 'SMSWiser shortlink CTR', group: 'smswiser', format: 'percent' },
  { id: 'deliveredRate', label: 'Delivered Rate', group: 'smswiser', format: 'ratio' },
  { id: 'undeliverableRate', label: 'Undeliverable Rate', group: 'smswiser', format: 'ratio' },
  { id: 'botRate', label: 'Bot Rate', group: 'smswiser', format: 'ratio' },
  // Both sides: tracker unique clicks per SMSWiser delivered SMS.
  { id: 'realCtr', label: 'Real CTR (uniques/delivered)', group: 'both', format: 'ratio' },
] as const;

export type GateMetricId = (typeof GATE_METRICS)[number]['id'];

export type GateMetricBag = Partial<Record<GateMetricId, number>>;

export type GateMetricGroup = (typeof GATE_METRICS)[number]['group'];

/** The picker's section heading for each group. */
export const GATE_METRIC_GROUP_LABELS: Record<GateMetricGroup, string> = {
  tracker: 'Tracker',
  smswiser: 'SMSWiser',
  both: 'Tracker + SMSWiser',
};

/** The tracker counters a drop keeps and a day adds up; every tracker rate is derived from them. */
export const GATE_TRACKER_COUNTERS = [
  'impressions',
  'visits',
  'uniqueVisits',
  'clicks',
  'uniqueClicks',
  'conversions',
  'registrations',
  'ftd',
  'revenue',
  'cost',
  'profit',
  'suspiciousClicks',
] as const satisfies readonly GateMetricId[];

/** SMSWiser counters a day adds up (smsCtr is SMSWiser's own per-drop rate). */
export const GATE_SMSWISER_COUNTERS = [
  'smsSent',
  'smsDelivered',
  'smsUndeliverable',
  'smsClicks',
  'smsBotClicks',
] as const satisfies readonly GateMetricId[];
